import { type Config, DEFAULT_CONFIG, readSettings, writeSettings } from "@codeshare/config";
import { type FormEvent, useRef, useState } from "react";
import {
  type ConnectionForm,
  formFromSettings,
  type IceRow,
  settingsFromForm,
} from "../settings/connection-form";
import { loadDeploymentConfig } from "../settings/deployment-config";
import { closeOnBackdrop } from "./dialog";
import { Icon } from "./Icon";

const storage = () => {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
};

const EMPTY_ROW: IceRow = { urls: "", username: "", credential: "" };
const iceList = (config: Config) =>
  config.iceServers.flatMap((s) => s.urls).join(", ") || "none (direct connections only)";
const relayList = (config: Config) =>
  config.signaling.flatMap((s) => (s.type === "nostr" ? s.relays : [])).join(", ");
const irohList = (config: Config) =>
  config.irohRelays.map((url) => url.replace(/^https:\/\//, "").replace(/\/$/, "")).join(", ") ||
  "none (no fallback)";

/** ICE servers (STUN/TURN), relay-only testing and signaling relays, kept in this browser. */
export function ConnectionSettings({
  roomHasPeople,
  onSaved,
}: {
  /** Others are in your room: new settings wait for the next room instead of restarting it. */
  roomHasPeople: boolean;
  onSaved: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<ConnectionForm>(() => formFromSettings({}));
  const [saved, setSaved] = useState(false);
  // The deployment's own config (defaults ← build env ← /config.json), shown as "the default".
  const [base, setBase] = useState<Config>(DEFAULT_CONFIG);
  const { problems } = settingsFromForm(form, base);
  const update = (patch: Partial<ConnectionForm>) => {
    setSaved(false);
    setForm((f) => ({ ...f, ...patch }));
  };
  const updateRow = (index: number, patch: Partial<IceRow>) =>
    update({
      iceServers: form.iceServers.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });

  const open = () => {
    setForm(formFromSettings(readSettings(storage())));
    void loadDeploymentConfig().then(setBase);
    setSaved(false);
    dialogRef.current?.showModal();
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    writeSettings(storage(), settingsFromForm(form, base).layer);
    setSaved(true);
    onSaved();
  };

  return (
    <>
      <button
        type="button"
        id="open-connection"
        className="activity-item"
        title="Connection settings"
        onClick={open}
      >
        <Icon name="plug" size={20} />
        <span className="visually-hidden">Connection</span>
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes dialogs natively; this only adds backdrop clicks for the mouse */}
      <dialog
        ref={dialogRef}
        onClick={closeOnBackdrop}
        className="dialog connection-dialog"
        aria-label="Connection settings"
      >
        <form onSubmit={save}>
          <h2>Connection settings</h2>

          <fieldset className="settings-group">
            <legend>ICE servers (STUN/TURN)</legend>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.customIce}
                onChange={(e) =>
                  update({
                    customIce: e.target.checked,
                    iceServers:
                      e.target.checked && form.iceServers.length === 0
                        ? [{ ...EMPTY_ROW }]
                        : form.iceServers,
                  })
                }
              />
              Use my own servers
            </label>
            {form.customIce ? (
              <>
                {form.iceServers.map((row, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity besides position
                  <div className="ice-row" key={i}>
                    <input
                      aria-label={`Server ${i + 1} URLs`}
                      placeholder="turn:turn.example.com:3478"
                      spellCheck={false}
                      value={row.urls}
                      onChange={(e) => updateRow(i, { urls: e.target.value })}
                    />
                    <input
                      aria-label={`Server ${i + 1} username`}
                      placeholder="username"
                      autoComplete="off"
                      value={row.username}
                      onChange={(e) => updateRow(i, { username: e.target.value })}
                    />
                    <input
                      aria-label={`Server ${i + 1} credential`}
                      placeholder="credential"
                      type="password"
                      autoComplete="off"
                      value={row.credential}
                      onChange={(e) => updateRow(i, { credential: e.target.value })}
                    />
                    <button
                      type="button"
                      className="link-button"
                      aria-label={`Remove server ${i + 1}`}
                      onClick={() =>
                        update({ iceServers: form.iceServers.filter((_, j) => j !== i) })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => update({ iceServers: [...form.iceServers, { ...EMPTY_ROW }] })}
                >
                  Add server
                </button>
              </>
            ) : (
              <p className="dialog-hint">Using the default: {iceList(base)}</p>
            )}
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.relayOnly}
                onChange={(e) => update({ relayOnly: e.target.checked })}
              />
              Relay only (connect through TURN; to test a TURN server)
            </label>
          </fieldset>

          <fieldset className="settings-group">
            <legend>Signaling (Nostr relays)</legend>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.customRelays}
                onChange={(e) => update({ customRelays: e.target.checked })}
              />
              Use my own relays
            </label>
            {form.customRelays ? (
              <textarea
                aria-label="Relay URLs, one per line"
                rows={3}
                spellCheck={false}
                placeholder="wss://relay.example.com"
                value={form.relays}
                onChange={(e) => update({ relays: e.target.value })}
              />
            ) : (
              <p className="dialog-hint">Using the default: {relayList(base)}</p>
            )}
          </fieldset>

          <fieldset className="settings-group">
            <legend>Fallback relay (iroh)</legend>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.fallbackRelay}
                onChange={(e) => update({ fallbackRelay: e.target.checked })}
              />
              Go through a relay when a direct connection fails
            </label>
            {form.fallbackRelay && (
              <>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.customIrohRelays}
                    onChange={(e) => update({ customIrohRelays: e.target.checked })}
                  />
                  Use my own iroh relays
                </label>
                {form.customIrohRelays ? (
                  <textarea
                    aria-label="iroh relay URLs, one per line"
                    rows={2}
                    spellCheck={false}
                    placeholder="https://relay.example.com"
                    value={form.irohRelays}
                    onChange={(e) => update({ irohRelays: e.target.value })}
                  />
                ) : (
                  <p className="dialog-hint">Using the default: {irohList(base)}</p>
                )}
              </>
            )}
          </fieldset>

          {problems.length > 0 && (
            <ul className="settings-problems" role="alert">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          <p className="dialog-hint">
            Saved in this browser only: never in room links or sent to anyone.{" "}
            {roomHasPeople
              ? "Others are in your room, so changes apply to your next room."
              : "Saving restarts your room with them (nobody has joined it yet)."}{" "}
            Rooms you join from a link use that link's relays.
          </p>
          {saved && (
            <p className="dialog-hint" role="status">
              Saved.
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" onClick={() => update(formFromSettings({}))}>
              Reset to defaults
            </button>
            <span className="top-bar-spacer" />
            <button type="button" onClick={() => dialogRef.current?.close()}>
              Close
            </button>
            <button type="submit" className="primary">
              Save
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
